import { Search, PlusCircle, ArrowLeft } from "@tamagui/lucide-icons";
import {
  Customer,
  ServiceCompany,
  useServiceRequest,
  useCreateServiceCompany,
  useHubs,
  useSensors,
  useServiceRequestFlow,
} from "common";
import React, { useState, useEffect } from "react";
import { Location, useNavigate, useParams, useLocation } from "react-router";
import {
  Button,
  Card,
  Input,
  Label,
  Paragraph,
  RadioGroup,
  Spinner,
  Text,
  XStack,
  YStack,
  getTokens,
  Select,
  H4,
  H5,
  H6,
} from "tamagui";

interface CustomerFormProps {
  onFindByEmail: (email: string) => Promise<Customer[]>;
  onCreateNew: (customer: any) => Promise<Customer | null>;
  customers: Customer[] | null;
  selectedCustomer: Customer | null;
  onSelectCustomer: (customer: Customer) => void;
  hasSearched?: boolean;
  lastSearchedEmail?: string;
  loading?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  onFindByEmail,
  onCreateNew,
  customers,
  selectedCustomer,
  onSelectCustomer,
  hasSearched = false,
  lastSearchedEmail = "",
  loading = false,
}) => {
  const [searchEmail, setSearchEmail] = useState(lastSearchedEmail);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "USA",
    companyName: "",
  });
  const [mode, setMode] = useState<"search" | "create">("search");

  useEffect(() => {
    setSearchEmail(lastSearchedEmail);
  }, [lastSearchedEmail]);

  const tokens = getTokens();

  const handleSearch = async () => {
    await onFindByEmail(searchEmail);
  };

  const handleCreateNew = async () => {
    await onCreateNew(newCustomer);
  };

  return (
    <YStack space="$4" padding="$4" $xs={{ padding: 0 }}>
      <H5 size="$11">Find or Create Customer</H5>

      <XStack gap="$4" alignItems="center" flexWrap="wrap">
        <Text>Mode:</Text>
        <RadioGroup
          value={mode}
          onValueChange={(val) => setMode(val as "search" | "create")}
          orientation="horizontal"
          flexWrap="wrap"
          accessibilityLabel="Customer mode"
        >
          <XStack space="$2" alignItems="center">
            <RadioGroup.Item value="search" id="search-mode">
              <RadioGroup.Indicator />
            </RadioGroup.Item>
            <Label htmlFor="search-mode">Find Existing</Label>
          </XStack>
          <XStack space="$2" alignItems="center">
            <RadioGroup.Item value="create" id="create-mode">
              <RadioGroup.Indicator />
            </RadioGroup.Item>
            <Label htmlFor="create-mode">Create New</Label>
          </XStack>
        </RadioGroup>
      </XStack>

      {mode === "search" && (
        <YStack space="$3">
          <XStack space="$2" alignItems="center">
            <Input
              flex={1}
              placeholder="Search by Email"
              value={searchEmail}
              onChangeText={setSearchEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <Button
              onPress={handleSearch}
              disabled={loading}
              icon={loading ? undefined : Search}
            >
              {loading ? <Spinner size="small" color="$color" /> : "Search"}
            </Button>
          </XStack>

          {customers && customers.length > 0 && (
            <YStack space="$3" marginTop="$3">
              <Paragraph>Select Customer:</Paragraph>
              <XStack flexWrap="wrap" space="$3" alignItems="flex-start">
                {customers.map((customer) => (
                  <Card
                    key={customer.id}
                    elevate
                    bordered
                    padding="$3"
                    width="48%"
                    $xs={{ width: "100%" }}
                    cursor="pointer"
                    borderColor={
                      selectedCustomer?.id === customer.id
                        ? (tokens.color.blue9Dark as any)
                        : (tokens.color.gray7Dark as any)
                    }
                    hoverStyle={{
                      borderColor: tokens.color.blue9Dark as any,
                    }}
                    onPress={() => onSelectCustomer(customer)}
                  >
                    <Card.Header>
                      <H6 size="$9.5">{customer.name}</H6>
                    </Card.Header>
                    <Paragraph theme="alt2" size="$3">
                      {customer.email}
                    </Paragraph>
                    <Paragraph theme="alt2" size="$3">
                      {customer.phone}
                    </Paragraph>
                    <Paragraph theme="alt2" size="$3">
                      {customer.address}
                    </Paragraph>
                  </Card>
                ))}
              </XStack>
            </YStack>
          )}
          {customers &&
            customers.length === 0 &&
            !loading &&
            hasSearched &&
            lastSearchedEmail &&
            lastSearchedEmail.trim() !== "" && (
              <Paragraph marginTop="$3">
                No customers found with that email.
              </Paragraph>
            )}
        </YStack>
      )}

      {mode === "create" && (
        <YStack space="$3">
          <XStack space="$3" $xs={{ flexDirection: "column" }}>
            <YStack flex={1} space="$2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Full Name"
                value={newCustomer.name}
                onChangeText={(text) =>
                  setNewCustomer({ ...newCustomer, name: text })
                }
              />
            </YStack>
            <YStack flex={1} space="$2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                placeholder="Email Address"
                keyboardType="email-address"
                autoCapitalize="none"
                value={newCustomer.email}
                onChangeText={(text) =>
                  setNewCustomer({ ...newCustomer, email: text })
                }
              />
            </YStack>
          </XStack>
          <XStack space="$3" $xs={{ flexDirection: "column" }}>
            <YStack flex={1} space="$2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="Phone Number"
                keyboardType="phone-pad"
                value={newCustomer.phone}
                onChangeText={(text) =>
                  setNewCustomer({ ...newCustomer, phone: text })
                }
              />
            </YStack>
            <YStack flex={1} space="$2">
              <Label htmlFor="companyName">Company Name (Optional)</Label>
              <Input
                id="companyName"
                placeholder="Company Name"
                value={newCustomer.companyName}
                onChangeText={(text) =>
                  setNewCustomer({ ...newCustomer, companyName: text })
                }
              />
            </YStack>
          </XStack>
          <YStack space="$2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              placeholder="Street Address"
              value={newCustomer.address}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, address: text })
              }
            />
          </YStack>
          <XStack space="$3" $xs={{ flexDirection: "column" }}>
            <YStack flex={1} space="$2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                placeholder="City"
                value={newCustomer.city}
                onChangeText={(text) =>
                  setNewCustomer({ ...newCustomer, city: text })
                }
              />
            </YStack>
            <YStack flex={1} space="$2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                placeholder="State"
                value={newCustomer.state}
                onChangeText={(text) =>
                  setNewCustomer({ ...newCustomer, state: text })
                }
              />
            </YStack>
            <YStack flex={1} space="$2">
              <Label htmlFor="zipCode">Zip Code</Label>
              <Input
                id="zipCode"
                placeholder="Zip Code"
                keyboardType="number-pad"
                value={newCustomer.zipCode}
                onChangeText={(text) =>
                  setNewCustomer({ ...newCustomer, zipCode: text })
                }
              />
            </YStack>
          </XStack>
          <Button
            onPress={handleCreateNew}
            disabled={loading}
            icon={loading ? undefined : PlusCircle}
            theme="active"
          >
            {loading ? <Spinner /> : "Create Customer"}
          </Button>
        </YStack>
      )}
    </YStack>
  );
};

interface ServiceCompanySelectorProps {
  companies: ServiceCompany[];
  onSelect: (serviceCompany: ServiceCompany) => void;
  selectedCompany: ServiceCompany | null;
  onBack: () => void;
  onCreateNew?: (company: any) => Promise<ServiceCompany | null>;
  loading?: boolean;
}

const ServiceCompanySelector: React.FC<ServiceCompanySelectorProps> = ({
  companies,
  onSelect,
  selectedCompany,
  onBack,
  onCreateNew,
}) => {
  const tokens = getTokens();
  const [mode, setMode] = useState<"select" | "create">("select");
  const [loading, setLoading] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const handleCreateNew = async () => {
    if (!onCreateNew) return;
    setLoading(true);
    try {
      const createdCompany = await onCreateNew(newCompany);
      if (createdCompany) {
        onSelect(createdCompany);
      }
    } catch (error) {
      console.error("Error creating service company:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <YStack space="$4" padding="$4" $xs={{ padding: 0 }}>
      <XStack
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="$2"
        marginBottom="$3"
      >
        <H5 size="$11">Select Service Company</H5>
        <Button icon={<ArrowLeft />} onPress={onBack} chromeless>
          Back to Customer
        </Button>
      </XStack>

      <XStack gap="$4" alignItems="center" flexWrap="wrap">
        <Text>Mode:</Text>
        <RadioGroup
          value={mode}
          onValueChange={(val) => setMode(val as "select" | "create")}
          orientation="horizontal"
          flexWrap="wrap"
          accessibilityLabel="Service company mode"
        >
          <XStack space="$2" alignItems="center">
            <RadioGroup.Item value="select" id="select-mode">
              <RadioGroup.Indicator />
            </RadioGroup.Item>
            <Label htmlFor="select-mode">Select Existing</Label>
          </XStack>
          <XStack space="$2" alignItems="center">
            <RadioGroup.Item value="create" id="create-mode">
              <RadioGroup.Indicator />
            </RadioGroup.Item>
            <Label htmlFor="create-mode">Create New</Label>
          </XStack>
        </RadioGroup>
      </XStack>

      {mode === "select" &&
        (companies && companies.length > 0 ? (
          <XStack
            flexWrap="wrap"
            space="$3"
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            {companies.map((company) => (
              <Card
                key={company.id}
                elevate
                bordered
                padding="$3"
                width="32%"
                minWidth={250}
                marginRight="$3"
                marginBottom="$3"
                $xs={{ width: "100%", minWidth: 0, marginRight: 0 }}
                cursor="pointer"
                borderColor={
                  selectedCompany?.id === company.id
                    ? (tokens.color.blue9Dark as any)
                    : (tokens.color.gray7Dark as any)
                }
                hoverStyle={{
                  borderColor: tokens.color.blue9Dark as any,
                }}
                onPress={() => onSelect(company)}
              >
                <Card.Header>
                  <H6 size="$9.5">{company.name}</H6>
                </Card.Header>
                <Paragraph theme="alt2" size="$3">
                  {company.address}
                </Paragraph>
                <Paragraph theme="alt2" size="$3">
                  {company.phone}
                </Paragraph>
                <Paragraph theme="alt2" size="$3">
                  {company.email}
                </Paragraph>
              </Card>
            ))}
          </XStack>
        ) : (
          <Paragraph theme="alt2" marginTop="$2">
            No service companies found.
          </Paragraph>
        ))}

      {mode === "create" && (
        <YStack space="$3">
          <XStack space="$3" $xs={{ flexDirection: "column" }}>
            <YStack flex={1} space="$2">
              <Label htmlFor="name">Company Name*</Label>
              <Input
                id="name"
                placeholder="Company Name"
                value={newCompany.name}
                onChangeText={(text) =>
                  setNewCompany({ ...newCompany, name: text })
                }
              />
            </YStack>
            <YStack flex={1} space="$2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                placeholder="Email Address"
                keyboardType="email-address"
                autoCapitalize="none"
                value={newCompany.email}
                onChangeText={(text) =>
                  setNewCompany({ ...newCompany, email: text })
                }
              />
            </YStack>
          </XStack>
          <XStack space="$3" $xs={{ flexDirection: "column" }}>
            <YStack flex={1} space="$2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="Phone Number"
                keyboardType="phone-pad"
                value={newCompany.phone}
                onChangeText={(text) =>
                  setNewCompany({ ...newCompany, phone: text })
                }
              />
            </YStack>
          </XStack>
          <YStack space="$2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              placeholder="Street Address"
              value={newCompany.address}
              onChangeText={(text) =>
                setNewCompany({ ...newCompany, address: text })
              }
            />
          </YStack>
          <XStack space="$3" $xs={{ flexDirection: "column" }}>
            <YStack flex={1} space="$2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                placeholder="City"
                value={newCompany.city}
                onChangeText={(text) =>
                  setNewCompany({ ...newCompany, city: text })
                }
              />
            </YStack>
            <YStack flex={1} space="$2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                placeholder="State"
                value={newCompany.state}
                onChangeText={(text) =>
                  setNewCompany({ ...newCompany, state: text })
                }
              />
            </YStack>
            <YStack flex={1} space="$2">
              <Label htmlFor="zipCode">Zip Code</Label>
              <Input
                id="zipCode"
                placeholder="Zip Code"
                keyboardType="number-pad"
                value={newCompany.zipCode}
                onChangeText={(text) =>
                  setNewCompany({ ...newCompany, zipCode: text })
                }
              />
            </YStack>
          </XStack>
          <Button
            onPress={handleCreateNew}
            disabled={loading}
            icon={loading ? undefined : PlusCircle}
            theme="active"
          >
            {loading ? <Spinner /> : "Create Service Company"}
          </Button>
        </YStack>
      )}
    </YStack>
  );
};

interface ServiceRequestFormProps {
  onSubmit: (data: any) => Promise<any>;
  onBack: () => void;
  editMode?: boolean;
  initialData?: any;
}

const ServiceRequestFormFields: React.FC<ServiceRequestFormProps> = ({
  onSubmit,
  onBack,
  editMode = false,
  initialData,
}) => {
  const { hubs = [], loading: hubsLoading } = useHubs({
    fetchAndSubscribeOnLoad: true,
  });
  const [selectedHubId, setSelectedHubId] = useState<string>(
    initialData?.hubId || "",
  );
  const { sensors = [], loading: sensorsLoading } = useSensors(selectedHubId);

  const [formData, setFormData] = useState({
    title: "",
    serviceType: "",
    description: "",
    urgency: "medium",
    requestedDate: "",
    requestedTimeWindow: "",
    notes: "",
    hubId: "",
    sensorId: "",
    ...initialData,
  });

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <YStack space="$4">
      <XStack
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="$2"
        marginBottom="$3"
      >
        <H6 size="$9.5">{editMode ? "Edit Service Request" : "Create Service Request"}</H6>
        <Button icon={<ArrowLeft />} onPress={onBack} chromeless>
          Back to Service Company
        </Button>
      </XStack>

      <YStack space="$4">
        <YStack>
          <Label htmlFor="title">Service Request Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.nativeEvent.text })
            }
          />
        </YStack>

        <XStack flexWrap="wrap" gap="$4">
          <YStack flex={1} minWidth={200}>
            <Label htmlFor="serviceType">Service Type *</Label>
            <Select
              id="serviceType"
              value={formData.serviceType}
              onValueChange={(value) =>
                setFormData({ ...formData, serviceType: value })
              }
            >
              <Select.Trigger>
                <Select.Value placeholder="Select service type" />
              </Select.Trigger>
              <Select.Content>
                <Select.ScrollUpButton />
                <Select.Viewport>
                  <Select.Item index={0} value="HVAC">
                    <Select.ItemText>HVAC</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={1} value="Plumbing">
                    <Select.ItemText>Plumbing</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={2} value="Electrical">
                    <Select.ItemText>Electrical</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={3} value="Leak">
                    <Select.ItemText>Leak</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={4} value="Filter">
                    <Select.ItemText>Filter</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={5} value="Other">
                    <Select.ItemText>Other</Select.ItemText>
                  </Select.Item>
                </Select.Viewport>
                <Select.ScrollDownButton />
              </Select.Content>
            </Select>
          </YStack>

          <YStack flex={1} minWidth={200}>
            <Label htmlFor="urgency">Urgency *</Label>
            <Select
              id="urgency"
              value={formData.urgency}
              onValueChange={(value) =>
                setFormData({ ...formData, urgency: value })
              }
            >
              <Select.Trigger>
                <Select.Value placeholder="Select urgency" />
              </Select.Trigger>
              <Select.Content>
                <Select.ScrollUpButton />
                <Select.Viewport>
                  <Select.Item index={0} value="low">
                    <Select.ItemText>Low</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={1} value="medium">
                    <Select.ItemText>Medium</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={2} value="high">
                    <Select.ItemText>High</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={3} value="emergency">
                    <Select.ItemText>Emergency</Select.ItemText>
                  </Select.Item>
                </Select.Viewport>
                <Select.ScrollDownButton />
              </Select.Content>
            </Select>
          </YStack>
        </XStack>

        <YStack>
          <Label htmlFor="description">Description *</Label>
          <Input
            id="description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.nativeEvent.text })
            }
            multiline
            numberOfLines={4}
          />
        </YStack>

        <XStack flexWrap="wrap" gap="$4">
          <YStack flex={1} minWidth={200}>
            <Label htmlFor="hubId">Base Station</Label>
            <Select
              id="hubId"
              value={formData.hubId}
              onValueChange={(value) => {
                setSelectedHubId(value);
                setFormData({ ...formData, hubId: value, sensorId: "" }); // Reset sensor when hub changes
              }}
            >
              <Select.Trigger>
                <Select.Value placeholder="Select base station" />
              </Select.Trigger>
              <Select.Content>
                <Select.ScrollUpButton />
                <Select.Viewport>
                  <Select.Item index={-1} value="">
                    <Select.ItemText>None</Select.ItemText>
                  </Select.Item>
                  {hubs.map((hub, index) => (
                    <Select.Item key={hub.id} index={index} value={hub.id}>
                      <Select.ItemText>{hub.name || hub.id}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
                <Select.ScrollDownButton />
              </Select.Content>
            </Select>
            {hubsLoading && (
              <Text fontSize="$2" color="$gray10">
                Loading hubs...
              </Text>
            )}
          </YStack>

          <YStack flex={1} minWidth={200}>
            <Label htmlFor="sensorId">Sensor</Label>
            <Select
              id="sensorId"
              value={formData.sensorId}
              onValueChange={(value) =>
                setFormData({ ...formData, sensorId: value })
              }
            >
              <Select.Trigger>
                <Select.Value
                  placeholder={
                    formData.hubId ? "Select sensor" : "Select a base station first"
                  }
                />
              </Select.Trigger>
              <Select.Content>
                <Select.ScrollUpButton />
                <Select.Viewport>
                  <Select.Item index={-1} value="">
                    <Select.ItemText>None</Select.ItemText>
                  </Select.Item>
                  {sensors.map((sensor, index) => (
                    <Select.Item
                      key={sensor.id}
                      index={index}
                      value={sensor.id}
                    >
                      <Select.ItemText>
                        {sensor.name || sensor.type || sensor.id}
                      </Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
                <Select.ScrollDownButton />
              </Select.Content>
            </Select>
            {sensorsLoading && (
              <Text fontSize="$2" color="$gray10">
                Loading sensors...
              </Text>
            )}
          </YStack>
        </XStack>

        <XStack flexWrap="wrap" gap="$4">
          <YStack flex={1} minWidth={200}>
            <Label htmlFor="requestedDate">Requested Date</Label>
            <Input
              id="requestedDate"
              value={formData.requestedDate}
              placeholder="YYYY-MM-DD"
              onChange={(e) =>
                setFormData({ ...formData, requestedDate: e.nativeEvent.text })
              }
            />
          </YStack>

          <YStack flex={1} minWidth={200}>
            <Label htmlFor="requestedTimeWindow">Requested Time Window</Label>
            <Select
              id="requestedTimeWindow"
              value={formData.requestedTimeWindow}
              onValueChange={(value) =>
                setFormData({ ...formData, requestedTimeWindow: value })
              }
            >
              <Select.Trigger>
                <Select.Value placeholder="Select time window" />
              </Select.Trigger>
              <Select.Content>
                <Select.ScrollUpButton />
                <Select.Viewport>
                  <Select.Item index={0} value="morning">
                    <Select.ItemText>Morning (8AM - 12PM)</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={1} value="afternoon">
                    <Select.ItemText>Afternoon (12PM - 4PM)</Select.ItemText>
                  </Select.Item>
                  <Select.Item index={2} value="evening">
                    <Select.ItemText>Evening (4PM - 8PM)</Select.ItemText>
                  </Select.Item>
                </Select.Viewport>
                <Select.ScrollDownButton />
              </Select.Content>
            </Select>
          </YStack>
        </XStack>

        <YStack>
          <Label htmlFor="notes">Additional Notes</Label>
          <Input
            id="notes"
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.nativeEvent.text })
            }
            multiline
            numberOfLines={4}
          />
        </YStack>

        <XStack
          justifyContent="flex-end"
          flexWrap="wrap"
          gap="$3"
          marginTop="$4"
        >
          <Button variant="outlined" onPress={onBack}>
            Back
          </Button>
          <Button onPress={handleSubmit} theme="active">
            {editMode ? "Update" : "Create"} Service Request
          </Button>
        </XStack>
      </YStack>
    </YStack>
  );
};

interface ConfirmationScreenProps {
  serviceRequest: any;
  onConfirm: () => Promise<any>;
  onBack: () => void;
}

const ConfirmationScreen: React.FC<ConfirmationScreenProps> = ({
  serviceRequest,
  onConfirm,
  onBack,
}) => (
  <YStack>
    <XStack
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap="$2"
      marginBottom="$4"
    >
      <H5 size="$11">Confirm Service Request</H5>
      <Button icon={<ArrowLeft />} onPress={onBack}>
        Back to Service Request
      </Button>
    </XStack>

    <Card padding="$4" marginBottom="$4">
      <H5 size="$11" marginBottom="$3">Service Request Details</H5>
      <YStack space="$2">
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Title
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.title}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Service Type
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.serviceType}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Urgency
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.urgency}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Requested Date/Time
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.requestedDate} {serviceRequest.requestedTimeWindow}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Description
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.description}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Additional Notes
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.notes || "None"}
          </Text>
        </YStack>
      </YStack>
    </Card>

    <Card padding="$4" marginBottom="$4">
      <H5 size="$11" marginBottom="$3">Customer Information</H5>
      <YStack space="$2">
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Name
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.customer?.name}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Email
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.customer?.email}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Phone
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.customer?.phone}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Address
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.customer?.address}
          </Text>
        </YStack>
      </YStack>
    </Card>

    <Card padding="$4" marginBottom="$4">
      <H5 size="$11" marginBottom="$3">Service Company</H5>
      <YStack space="$2">
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Name
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.serviceCompany?.name}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Phone
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.serviceCompany?.phone}
          </Text>
        </YStack>
        <YStack>
          <Text color="$gray10" fontSize="$3" fontWeight="bold">
            Address
          </Text>
          <Text fontSize="$4" marginBottom="$2">
            {serviceRequest.serviceCompany?.address}
          </Text>
        </YStack>
      </YStack>
    </Card>

    <XStack justifyContent="flex-end" flexWrap="wrap" gap="$3" marginTop="$4">
      <Button variant="outlined" onPress={onBack}>
        Back
      </Button>
      <Button theme="active" onPress={onConfirm}>
        Confirm and Submit
      </Button>
    </XStack>
  </YStack>
);

const CompletionScreen: React.FC<{
  serviceRequest: any;
  onNewRequest: () => void;
}> = ({ serviceRequest, onNewRequest }) => {
  const navigate = useNavigate();
  const isValidServiceRequest = serviceRequest && serviceRequest.id;

  return (
    <YStack alignItems="center" padding="$4">
      <H4 size="$13.5" marginBottom="$2">Service Request Created!</H4>
      <Paragraph textAlign="center" marginBottom="$2">
        Your service request has been successfully created and sent to the
        service company.
      </Paragraph>
      {isValidServiceRequest ? (
        <>
          <Paragraph textAlign="center" marginBottom="$4">
            Service Request ID:{" "}
            <Text fontWeight="bold">{serviceRequest.id}</Text>
          </Paragraph>
          <XStack
            marginTop="$4"
            justifyContent="center"
            flexWrap="wrap"
            gap="$3"
          >
            <Button
              variant="outlined"
              onPress={() => navigate(`/service-requests/${serviceRequest.id}`)}
            >
              View Service Request
            </Button>
            <Button theme="active" onPress={onNewRequest}>
              Create Another Request
            </Button>
          </XStack>
        </>
      ) : (
        <>
          <Paragraph textAlign="center" marginBottom="$4" theme="alt2">
            (Service request details not available)
          </Paragraph>
          <XStack
            marginTop="$4"
            justifyContent="center"
            flexWrap="wrap"
            gap="$3"
          >
            <Button
              variant="outlined"
              onPress={() => navigate("/service-requests")}
            >
              View All Service Requests
            </Button>
            <Button theme="active" onPress={onNewRequest}>
              Create Another Request
            </Button>
          </XStack>
        </>
      )}
    </YStack>
  );
};

const ServiceRequestForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { state: locationState } = useLocation() as Location<
    | {
        customerId?: string;
        serviceCompanyId?: string;
        alertId?: string;
        hubId?: string;
        sensorId?: string;
      }
    | undefined
  >;
  const {
    alertId,
    customerId: stateCustomerId,
    serviceCompanyId,
    hubId,
    sensorId,
  } = locationState ?? {};

  const urlParams = new URLSearchParams(window.location.search);
  const urlCustomerId = urlParams.get("customerId");

  const customerId = urlCustomerId || stateCustomerId;
  const isEditMode = Boolean(id);

  // Use the createServiceCompany hook from common package
  const { create: createServiceCompany, loading: createServiceCompanyLoading } =
    useCreateServiceCompany();

  const {
    state,
    loading,
    error,
    customers,
    serviceCompanies,
    findCustomerByEmail,
    addNewCustomer,
    selectServiceCompany,
    createNewServiceRequest,
    confirmServiceRequest,
    resetFlow,
    goBack,
  } = useServiceRequestFlow({ customerId, serviceCompanyId });

  const selectCustomer = (
    useServiceRequestFlow({ customerId, serviceCompanyId }) as any
  ).selectCustomer;

  const { serviceRequest: existingServiceRequest } = useServiceRequest(
    id || "",
  );

  useEffect(() => {
    // If in edit mode, initialize the flow with existing data
    if (isEditMode && existingServiceRequest) {
      // Logic to initialize the flow with existing data
    }
  }, [isEditMode, existingServiceRequest]);

  const handleSelectCustomer = (customer: Customer) => {
    if (state.step === "customer") {
      if (typeof selectCustomer === "function") {
        selectCustomer(customer);
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set("customerId", customer.id);
        window.location.href = url.toString();
      }
    }
  };

  const renderStep = () => {
    switch (state.step) {
      case "customer":
        return (
          <CustomerForm
            onFindByEmail={findCustomerByEmail}
            onCreateNew={addNewCustomer}
            customers={customers}
            selectedCustomer={state.customer}
            onSelectCustomer={handleSelectCustomer}
            hasSearched={false}
            lastSearchedEmail=""
          />
        );
      case "serviceCompany":
        return (
          <ServiceCompanySelector
            companies={serviceCompanies}
            onSelect={selectServiceCompany}
            selectedCompany={state.serviceCompany}
            onBack={goBack}
            onCreateNew={createServiceCompany}
            loading={createServiceCompanyLoading}
          />
        );
      case "serviceRequest":
        return (
          <ServiceRequestFormFields
            onSubmit={createNewServiceRequest}
            onBack={goBack}
            editMode={isEditMode}
            initialData={{
              customerId,
              serviceCompanyId,
              hubId,
              sensorId,
              alertId,
              ...existingServiceRequest,
            }}
          />
        );
      case "confirmation":
        return (
          <ConfirmationScreen
            serviceRequest={state.serviceRequest}
            onConfirm={confirmServiceRequest}
            onBack={goBack}
          />
        );
      case "complete":
        return (
          <CompletionScreen
            serviceRequest={state.serviceRequest}
            onNewRequest={resetFlow}
          />
        );
      default:
        return null;
    }
  };

  return (
    <YStack
      padding="$4"
      space="$4"
      marginHorizontal="auto"
      $sm={{ paddingHorizontal: 0 }}
    >
      <H4 size="$13.5">{isEditMode ? "Edit Service Request" : "Create Service Request"}</H4>

      <Text fontSize="$10" fontWeight="bold" marginBottom="$3">
        Current Step: {state.step.charAt(0).toUpperCase() + state.step.slice(1)}
      </Text>

      <Card elevate bordered padding="$4" $xs={{ padding: "$3" }}>
        {loading ? (
          <XStack
            space
            flex={1}
            justifyContent="center"
            alignItems="center"
            paddingVertical="$10"
          >
            <Spinner size="large" />
            <Paragraph marginLeft="$2">Loading...</Paragraph>
          </XStack>
        ) : error ? (
          <Paragraph color="$red10">Error: {error.message}</Paragraph>
        ) : (
          renderStep()
        )}
      </Card>
    </YStack>
  );
};

export default ServiceRequestForm;
